import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ValidationError } from '@shared/models/validation-error';
export class SetErrors {
  static readonly type = '[Error] Set Errors';
  constructor(public payload: ValidationError[]) {}
}

export class ClearErrors {
  static readonly type = '[Error] Clear Errors';
}

export class ClearErrorsByQuestionId {
  static readonly type = '[Error] Clear Errors By Question ID';
  constructor(public questionId: string) {}
}

export interface ErrorStateModel {
  errors: ValidationError[];
}

@State<ErrorStateModel>({
  name: 'errorState',
  defaults: {
    errors: [],
  },
})
@Injectable()
export class ErrorState {
  @Selector()
  static getErrors(state: ErrorStateModel): ValidationError[] {
    return state.errors;
  }

  @Selector()
  static getErrorsByQuestionId(
    state: ErrorStateModel,
  ): (questionId: string) => ValidationError[] {
    return (questionId: string) =>
      state.errors.filter((error) => error.questionId === questionId);
  }

  @Selector()
  static getErrorsByErrorType(
    state: ErrorStateModel,
  ): (errorType: string) => ValidationError | undefined {
    return (errorType: string) =>
      state.errors.find((error) => error.errorType === errorType);
  }

  @Action(SetErrors)
  setErrors(ctx: StateContext<ErrorStateModel>, action: SetErrors): void {
    const state = ctx.getState();
    const existingErrors = state.errors;

    // Remove existing errors for the same questionIds that are in the new payload
    const newQuestionIds = new Set(action.payload.map((e) => e.questionId));
    const filteredExistingErrors = existingErrors.filter(
      (error) => !newQuestionIds.has(error.questionId),
    );

    // Merge filtered existing errors with new errors
    const mergedErrors = [...filteredExistingErrors, ...action.payload];

    ctx.patchState({ errors: mergedErrors });
  }

  @Action(ClearErrors)
  clearErrors(ctx: StateContext<ErrorStateModel>): void {
    ctx.patchState({ errors: [] });
  }

  @Action(ClearErrorsByQuestionId)
  clearErrorsByQuestionId(
    ctx: StateContext<ErrorStateModel>,
    action: ClearErrorsByQuestionId,
  ): void {
    const state = ctx.getState();
    const filteredErrors = state.errors.filter(
      (error) => error.questionId !== action.questionId,
    );
    ctx.patchState({ errors: filteredErrors });
  }
}
